import type { FilterOption } from '@/components/common-table/FilterSearchBar'
import { CUSTOM_VIEWS_STORAGE_KEY_PREFIX } from '@/config/storageKeys'
import { useCurrentTenant } from '@/stores/authStore'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useCustomViewsContext } from './CustomViewsContext'

export interface CustomView {
	id: string
	name: string
	filters: FilterOption[]
	createdAt: number
}

interface CustomViewsStorage {
	[page: string]: CustomView[]
}

export function useCustomViews(pageKey: string) {
	const tenantId = useCurrentTenant()
	// Organization-scoped pages have no project/tenant context. On org-level routes `tenantId`
	// may still hold a fallback value, so suffixing the storage key with it would bind views to
	// an arbitrary tenant and make them vanish on tenant fallback changes. Bypass the suffix to
	// keep these keys stable. `organization-meters-*` covers every per-module meters bucket.
	const isOrgScoped =
		pageKey === 'inventory' || pageKey === 'system-notifications' || pageKey.startsWith('organization-meters-')
	const storageKey =
		tenantId && !isOrgScoped ? `${CUSTOM_VIEWS_STORAGE_KEY_PREFIX}_${tenantId}` : CUSTOM_VIEWS_STORAGE_KEY_PREFIX

	const [customViews, setCustomViews] = useState<CustomView[]>([])
	const { activeViewId, setActiveViewId } = useCustomViewsContext()

	const prevPageKeyRef = useRef(pageKey)
	const prevStorageKeyRef = useRef(storageKey)

	useEffect(() => {
		// Reset active view only when switching between valid, initialized pages or tenants.
		// Avoid resetting activeViewId to null during temporary key transitions (e.g., pageKey going to/from '')
		const isInitTransition = !prevPageKeyRef.current || !pageKey
		if (!isInitTransition) {
			if (prevPageKeyRef.current !== pageKey) {
				setActiveViewId(null)
			} else if (prevStorageKeyRef.current && storageKey && prevStorageKeyRef.current !== storageKey) {
				setActiveViewId(null)
			}
		}

		prevPageKeyRef.current = pageKey
		prevStorageKeyRef.current = storageKey

		const stored = localStorage.getItem(storageKey)
		if (stored) {
			try {
				const allViews: CustomViewsStorage = JSON.parse(stored)
				setCustomViews(allViews[pageKey] || [])
			} catch (error) {
				console.error('Failed to parse custom views:', error)
				setCustomViews([])
			}
		} else {
			setCustomViews([])
		}
	}, [pageKey, storageKey, setActiveViewId])

	// Save views to localStorage
	const saveToStorage = useCallback(
		(views: CustomView[]) => {
			const stored = localStorage.getItem(storageKey)
			let allViews: CustomViewsStorage = {}

			if (stored) {
				try {
					allViews = JSON.parse(stored)
				} catch (error) {
					console.error('Failed to parse existing views:', error)
				}
			}

			allViews[pageKey] = views
			localStorage.setItem(storageKey, JSON.stringify(allViews))
		},
		[pageKey, storageKey],
	)

	// Create a new custom view
	const createView = useCallback(
		(name: string, filters: FilterOption[]) => {
			const newView: CustomView = {
				id: `view_${Date.now()}`,
				name: name.trim(),
				filters,
				createdAt: Date.now(),
			}

			const updatedViews = [...customViews, newView]
			setCustomViews(updatedViews)
			saveToStorage(updatedViews)
			setActiveViewId(newView.id)

			return newView
		},
		[customViews, saveToStorage, setActiveViewId],
	)

	// Update an existing view
	const updateView = useCallback(
		(viewId: string, updates: { name?: string; filters?: FilterOption[] }) => {
			const updatedViews = customViews.map(view =>
				view.id === viewId
					? {
							...view,
							name: updates.name !== undefined ? updates.name.trim() : view.name,
							filters: updates.filters !== undefined ? updates.filters : view.filters,
						}
					: view,
			)
			setCustomViews(updatedViews)
			saveToStorage(updatedViews)
		},
		[customViews, saveToStorage],
	)

	// Delete a view
	const deleteView = useCallback(
		(viewId: string) => {
			const updatedViews = customViews.filter(view => view.id !== viewId)
			setCustomViews(updatedViews)
			saveToStorage(updatedViews)

			if (activeViewId === viewId) {
				setActiveViewId(null)
			}
		},
		[customViews, saveToStorage, activeViewId, setActiveViewId],
	)

	// Apply a view (returns its filters)
	const applyView = useCallback(
		(viewId: string) => {
			const view = customViews.find(v => v.id === viewId)
			if (view) {
				setActiveViewId(viewId)
				return view.filters
			}
			return []
		},
		[customViews, setActiveViewId],
	)

	// Clear active view
	const clearActiveView = useCallback(() => {
		setActiveViewId(null)
	}, [setActiveViewId])

	const reorderViews = useCallback(
		(reorderedViews: CustomView[]) => {
			setCustomViews(reorderedViews)
			saveToStorage(reorderedViews)
		},
		[saveToStorage],
	)

	return {
		customViews,
		activeViewId,
		createView,
		updateView,
		deleteView,
		applyView,
		reorderViews,
		clearActiveView,
	}
}
